import * as React from 'react';
import { FormattedMessage, useIntl } from 'react-intl';

import {
  ActionButton,
  ButtonModel,
  IconRegularChevronRight,
  IconRegularTrashBin,
  Text,
} from '@notino/react-styleguide';

import { useWishlistActionsFactory } from '@components/Universals/WishlistModifier/useWishlistActions';
import { useWishlistLists } from '@components/Universals/WishlistModifier/useWishlistLists';
import { useWishlistSelectionDrawer } from '@components/Universals/WishlistModifier/useWishlistSelectionDrawer';
import { useListById } from '@containers/WishList/hooks/useListById';
import { useWishlistStore } from '@containers/WishList/store';
import { trackWishlistChange } from '@containers/WishList/tracking';

import { selectionModeMessages } from './messages';
import {
  BottomContainer,
  ButtonsWrapper,
  CloseButton,
  CloseButtonWrapper,
  Container,
} from './styled';
import { useOpenBulkRemoveProductsDrawer } from './useOpenBulkRemoveProductsDrawer';

export const SelectionMode = () => {
  const { formatMessage } = useIntl();
  const { listById } = useListById();
  const selectionMode = useWishlistStore((x) => x.selectionMode);
  const selectedIds = useWishlistStore((x) => x.selectedIds);
  const products = useWishlistStore((state) => state.products);
  const { toggleSelectionMode, bulkRemoveProducts } = useWishlistStore(
    (x) => x.actions
  );

  const { open: openBulkRemoveProductsDrawer } =
    useOpenBulkRemoveProductsDrawer();

  const { handleWishlistClick } = useWishlistActionsFactory({ selectionMode });

  const { fetchLists, lastFetchedListsRef } = useWishlistLists();

  const { open: openListSelection, close } = useWishlistSelectionDrawer({
    texts: {
      saveButton: formatMessage(selectionModeMessages.moveSaveButton),
      createAndSaveButton: formatMessage(
        selectionModeMessages.createAndMoveSaveButton
      ),
    },
    onSave: async ({ addedToLists }) => {
      await handleWishlistClick({
        addToLists: addedToLists,
        removeFromLists: [{ id: listById.id }],
        productIds: selectedIds,
        lists: lastFetchedListsRef.current ?? [],
        actionLocation: 'wishlist',
        doNotTrack: true,
      });
      bulkRemoveProducts(selectedIds);
      toggleSelectionMode();
      trackWishlistChange({
        products: selectedIds.map((id) => {
          const product = products.find((p) => p?.id === id);
          return { product_code: product?.productCode || '' };
        }),
        interaction: 'move_button',
      });
      close();
    },
    interactionSource: 'move_button',
  });

  const handleCloseButtonKeyDown = (
    event: React.KeyboardEvent<HTMLSpanElement>
  ) => {
    if (event.key !== 'Enter' && event.key !== ' ') {
      return;
    }

    event.preventDefault();
    event.stopPropagation();
    toggleSelectionMode();
  };

  if (!selectionMode) return null;

  return (
    <BottomContainer>
      <Container>
        <CloseButtonWrapper
          role="button"
          tabIndex={0}
          onClick={toggleSelectionMode}
          onKeyDown={handleCloseButtonKeyDown}
        >
          <CloseButton />
        </CloseButtonWrapper>

        <Text
          typography="labelRegular400"
          textAlign="center"
          block={true}
          marginBottom="12px"
        >
          <FormattedMessage
            {...selectionModeMessages.selectedNumOfProducts}
            values={{ count: selectedIds.length }}
          />
        </Text>

        <ButtonsWrapper disabled={selectedIds.length === 0}>
          <ActionButton
            disabled={selectedIds.length === 0}
            buttonStyle={ButtonModel.Styles.secondary}
            icon={<IconRegularChevronRight />}
            onClick={async () => {
              const lists = await fetchLists();
              openListSelection({
                selectionMode: 'single',
                productId: selectedIds,
                lists: lists.filter((l) => l.id !== listById.id),
                productName: '',
              });
            }}
          >
            <FormattedMessage {...selectionModeMessages.moveButton} />
          </ActionButton>
          <ActionButton
            disabled={selectedIds.length === 0}
            buttonStyle={ButtonModel.Styles.secondary}
            icon={
              <IconRegularTrashBin
                color={
                  selectedIds.length === 0 ? 'icon.disabled' : 'icon.primary'
                }
              />
            }
            onClick={() =>
              openBulkRemoveProductsDrawer({ productIds: selectedIds })
            }
          >
            <FormattedMessage {...selectionModeMessages.removeButton} />
          </ActionButton>
        </ButtonsWrapper>
      </Container>
    </BottomContainer>
  );
};
